import {
  mdiChevronDown,
  mdiChevronRight,
  mdiCog,
  mdiDrag,
  mdiPencil,
  mdiPencilOff,
  mdiPin,
  mdiPlus,
} from '../components/icons';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { Api, type Link, type Section } from '../api';
import { LinkIconView, Svg } from '../components/Icon';
import { LinkEditor } from '../components/LinkEditor';
import { linkOpensInNewTab, SearchBar } from '../components/SearchBar';
import { SectionEditor } from '../components/SectionEditor';
import { Spinner } from '../components/ui';
import { useSortable, type SortEnd } from '../lib/useSortable';
import { attempt, editMode, loadPage, page, settings, user } from '../state';

type Editing =
  | { kind: 'link'; link?: Link; sectionId?: string }
  | { kind: 'section'; section?: Section }
  | null;

function greeting(hour: number) {
  if (hour < 5) return 'Good night';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(t);
  }, []);
  return (
    <p class="clock">
      {new Intl.DateTimeFormat(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(now)}
    </p>
  );
}

function LinkItem({
  link,
  layout,
  onEdit,
}: {
  link: Link;
  layout: 'tiles' | 'list';
  onEdit: (link: Link) => void;
}) {
  const content = (
    <>
      <LinkIconView icon={link.icon} title={link.title} size={layout === 'tiles' ? 32 : 18} />
      <span class="link-text">
        <span class="link-title">{link.title}</span>
        {layout === 'tiles' && link.description && (
          <span class="link-desc">{link.description}</span>
        )}
      </span>
      {link.pinned && editMode.value && <Svg path={mdiPin} size={14} label="Pinned" />}
    </>
  );
  return (
    <li class="link-item" data-id={link.id}>
      {editMode.value ? (
        <button type="button" class="link" onClick={() => onEdit(link)} title={`Edit ${link.title}`}>
          {content}
        </button>
      ) : (
        <a
          class="link"
          href={link.url}
          title={link.description || link.url}
          target={linkOpensInNewTab(link) ? '_blank' : undefined}
          rel="noopener noreferrer"
        >
          {content}
        </a>
      )}
    </li>
  );
}

function SectionCard({
  section,
  onEdit,
  onEditLink,
  onAddLink,
  onMoveLink,
}: {
  section: Section;
  onEdit: () => void;
  onEditLink: (link: Link) => void;
  onAddLink: () => void;
  onMoveLink: (e: SortEnd) => void;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const editing = editMode.value;
  useSortable(listRef, editing, { group: 'links', draggable: '.link-item' }, onMoveLink);
  const collapsed = section.collapsed && !editing;

  async function toggleCollapsed() {
    await attempt(() => Api.updateSection(section.id, { collapsed: !section.collapsed }));
    await loadPage();
  }

  return (
    <section
      class={`section section-${section.layout}${section.pinned ? '' : ' section-hidden'}`}
      data-id={section.id}
      aria-labelledby={`section-${section.id}`}
    >
      <header class="section-header">
        {editing && (
          <span class="section-drag" title="Drag to reorder">
            <Svg path={mdiDrag} size={18} />
          </span>
        )}
        <button
          type="button"
          class="section-toggle"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          disabled={editing}
        >
          {!editing && <Svg path={collapsed ? mdiChevronRight : mdiChevronDown} size={18} />}
          <h2 id={`section-${section.id}`}>{section.name}</h2>
          {!section.pinned && <span class="badge">hidden</span>}
        </button>
        {editing && (
          <span class="section-actions">
            <button type="button" class="icon-button" onClick={onAddLink} aria-label={`Add link to ${section.name}`}>
              <Svg path={mdiPlus} size={18} />
            </button>
            <button type="button" class="icon-button" onClick={onEdit} aria-label={`Edit ${section.name}`}>
              <Svg path={mdiPencil} size={18} />
            </button>
          </span>
        )}
      </header>
      {!collapsed && (
        <ul
          ref={listRef}
          class={`links links-${section.layout}`}
          data-container-id={section.id}
          data-empty-text={editing ? 'Drop links here' : 'No links yet'}
        >
          {section.links.map((link) => (
            <LinkItem key={link.id} link={link} layout={section.layout} onEdit={onEditLink} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function Home() {
  const { route } = useLocation();
  const [editing, setEditing] = useState<Editing>(null);
  const [showHidden, setShowHidden] = useState(false);
  const sectionsRef = useRef<HTMLDivElement>(null);
  const data = page.value;
  const s = settings.value;
  const isEditing = editMode.value;

  useEffect(() => {
    if (!page.value) void attempt(loadPage);
  }, []);

  const allLinks = useMemo(() => data?.sections.flatMap((x) => x.links) ?? [], [data]);
  const pinnedLinks = allLinks.filter((l) => l.pinned);
  const hiddenCount = data?.sections.filter((x) => !x.pinned).length ?? 0;
  const visible = data?.sections.filter((x) => x.pinned || isEditing || showHidden) ?? [];

  useSortable(
    sectionsRef,
    isEditing,
    { handle: '.section-drag', draggable: '.section' },
    ({ itemId, newIndex }) => {
      if (!data) return;
      // Only visible sections are rendered (all of them in edit mode).
      const ids = data.sections.map((x) => x.id).filter((id) => id !== itemId);
      ids.splice(newIndex, 0, itemId);
      const byId = new Map(data.sections.map((x) => [x.id, x]));
      page.value = { ...data, sections: ids.map((id) => byId.get(id)!) };
      void attempt(() => Api.orderSections(ids)).then(loadPage);
    }
  );

  function moveLink({ itemId, fromId, toId, newIndex }: SortEnd) {
    if (!data) return;
    const link = allLinks.find((l) => l.id === itemId);
    if (!link) return;
    const sections = data.sections.map((sec) => ({
      ...sec,
      links: sec.links.filter((l) => l.id !== itemId),
    }));
    const target = sections.find((sec) => sec.id === toId)!;
    target.links.splice(newIndex, 0, { ...link, sectionId: toId });
    page.value = { ...data, sections };
    void attempt(async () => {
      if (fromId !== toId) await Api.updateLink(itemId, { sectionId: toId });
      await Api.orderLinks(toId, target.links.map((l) => l.id));
    }).then(loadPage);
  }

  if (!data) return <Spinner />;
  const hour = new Date().getHours();
  const empty = data.sections.length === 0;

  return (
    <main
      class={`home density-${s.layout.density}${isEditing ? ' is-editing' : ''}`}
      style={{ '--columns': String(s.layout.columns) }}
    >
      <header class="home-header">
        <div>
          <h1 class="greeting">
            {greeting(hour)}, {user.value?.displayName}
          </h1>
          {s.layout.showClock && <Clock />}
        </div>
        <nav class="home-actions">
          <button
            type="button"
            class={`icon-button${isEditing ? ' active' : ''}`}
            onClick={() => (editMode.value = !isEditing)}
            aria-pressed={isEditing}
            aria-label={isEditing ? 'Done editing' : 'Edit page'}
            title={isEditing ? 'Done editing' : 'Edit page'}
          >
            <Svg path={isEditing ? mdiPencilOff : mdiPencil} />
          </button>
          <button
            type="button"
            class="icon-button"
            onClick={() => route('/settings')}
            aria-label="Settings"
            title="Settings"
          >
            <Svg path={mdiCog} />
          </button>
        </nav>
      </header>

      {s.layout.showSearch && <SearchBar links={allLinks} />}

      {isEditing && (
        <div class="edit-bar">
          <span>Editing: click a link to change it, drag to reorder or move between sections.</span>
          <span class="spacer" />
          <button type="button" class="button" onClick={() => setEditing({ kind: 'section' })}>
            <Svg path={mdiPlus} size={16} /> Section
          </button>
          <button type="button" class="button button-primary" onClick={() => setEditing({ kind: 'link' })}>
            <Svg path={mdiPlus} size={16} /> Link
          </button>
        </div>
      )}

      {pinnedLinks.length > 0 && !isEditing && (
        <ul class="links links-tiles pinned-links" aria-label="Pinned links">
          {pinnedLinks.map((link) => (
            <LinkItem key={link.id} link={link} layout="tiles" onEdit={() => {}} />
          ))}
        </ul>
      )}

      {empty ? (
        <div class="empty">
          <h2>Your page is empty</h2>
          <p>Add a link, import your browser bookmarks, or connect Claude or Grok to add links for you.</p>
          <div class="empty-actions">
            <button type="button" class="button button-primary" onClick={() => setEditing({ kind: 'link' })}>
              Add a link
            </button>
            <button type="button" class="button" onClick={() => setEditing({ kind: 'section' })}>
              Add a section
            </button>
            <button type="button" class="button" onClick={() => route('/settings/data')}>
              Import bookmarks
            </button>
          </div>
        </div>
      ) : (
        <div class="sections" ref={sectionsRef}>
          {visible.map((section) => (
            <SectionCard
              key={section.id}
              section={section}
              onEdit={() => setEditing({ kind: 'section', section })}
              onEditLink={(link) => setEditing({ kind: 'link', link })}
              onAddLink={() => setEditing({ kind: 'link', sectionId: section.id })}
              onMoveLink={moveLink}
            />
          ))}
        </div>
      )}

      {hiddenCount > 0 && !isEditing && (
        <button type="button" class="link-button show-hidden" onClick={() => setShowHidden(!showHidden)}>
          {showHidden ? 'Hide' : 'Show'} {hiddenCount} hidden section{hiddenCount === 1 ? '' : 's'}
        </button>
      )}

      {editing?.kind === 'link' && (
        <LinkEditor
          link={editing.link}
          sections={data.sections}
          defaultSectionId={editing.sectionId}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.kind === 'section' && (
        <SectionEditor section={editing.section} onClose={() => setEditing(null)} />
      )}
    </main>
  );
}
